import { createSlice } from '@reduxjs/toolkit';
import axios from 'utils/axios';

// ---------- Initial State ----------
export const kanbanInitialState = {
  loading: false,
  error: null,
  columns: [],
  columnsOrder: [],
  comments: [],
  items: [],
  profiles: [],
  selectedItem: null,
  userStory: [],
  userStoryOrder: [],
  lastLoadedAt: null
};

// ---------- Helpers ----------
const normalizeId = (raw) => (typeof raw === 'number' ? raw : String(raw ?? '0'));

const normalizeColumns = (columns = []) =>
  columns.map((c) => ({
    id: normalizeId(c.id),
    title: c.title ?? '',
    itemIds: Array.isArray(c.itemIds) ? c.itemIds.map(normalizeId) : []
  }));

const normalizeItems = (items = []) =>
  items.map((i) => ({
    id: normalizeId(i.id),
    title: i.title ?? '',
    dueDate: i.dueDate ?? null,
    image: i.image ?? null,
    assign: i.assign ?? '',
    description: i.description ?? '',
    priority: i.priority ?? 'low',
    attachments: i.attachments ?? [],
    commentIds: Array.isArray(i.commentIds) ? i.commentIds.map(normalizeId) : []
  }));

const normalizeStories = (stories = []) =>
  stories.map((s) => ({
    id: normalizeId(s.id),
    title: s.title ?? '',
    itemIds: Array.isArray(s.itemIds) ? s.itemIds.map(normalizeId) : [],
    commentIds: Array.isArray(s.commentIds) ? s.commentIds.map(normalizeId) : []
  }));

const normalizeComments = (comments = []) =>
  comments.map((c) => ({
    id: normalizeId(c.id),
    comment: c.comment ?? '',
    profileId: c.profileId != null ? normalizeId(c.profileId) : null,
    itemId: c.itemId != null ? normalizeId(c.itemId) : null,
    storyId: c.storyId != null ? normalizeId(c.storyId) : null
  }));

const normalizeProfiles = (profiles = []) =>
  profiles.map((p) => ({
    id: normalizeId(p.id),
    name: p.name ?? '',
    avatar: p.avatar ?? '',
    time: p.time ?? null
  }));

// ---------- Slice ----------
const slice = createSlice({
  name: 'kanban',
  initialState: kanbanInitialState,
  reducers: {
    startLoading(state) {
      state.loading = true;
      state.error = null;
    },
    hasError(state, action) {
      state.loading = false;
      state.error = action.payload;
    },

    // GET success reducers
    getColumnsSuccess(state, action) {
      state.columns = normalizeColumns(action.payload || []);
      state.loading = false;
      state.lastLoadedAt = Date.now();
    },
    getColumnsOrderSuccess(state, action) {
      state.columnsOrder = (action.payload || []).map(normalizeId);
      state.loading = false;
    },
    getCommentsSuccess(state, action) {
      state.comments = normalizeComments(action.payload || []);
      state.loading = false;
    },
    getProfilesSuccess(state, action) {
      state.profiles = normalizeProfiles(action.payload || []);
      state.loading = false;
    },
    getItemsSuccess(state, action) {
      state.items = normalizeItems(action.payload || []);
      state.loading = false;
    },
    getUserStorySuccess(state, action) {
      state.userStory = normalizeStories(action.payload || []);
      state.loading = false;
    },
    getUserStoryOrderSuccess(state, action) {
      state.userStoryOrder = (action.payload || []).map(normalizeId);
      state.loading = false;
    },

    // Column mutations
    addColumnSuccess(state, action) {
      state.columns = normalizeColumns(action.payload.columns || []);
      state.columnsOrder = (action.payload.columnsOrder || []).map(normalizeId);
      state.loading = false;
    },
    editColumnSuccess(state, action) {
      state.columns = normalizeColumns(action.payload.columns || []);
      state.loading = false;
    },
    updateColumnOrderSuccess(state, action) {
      state.columnsOrder = (action.payload.columnsOrder || []).map(normalizeId);
      state.loading = false;
    },
    deleteColumnSuccess(state, action) {
      state.columns = normalizeColumns(action.payload.columns || []);
      state.columnsOrder = (action.payload.columnsOrder || []).map(normalizeId);
      state.loading = false;
    },

    // Item mutations
    addItemSuccess(state, action) {
      state.items = normalizeItems(action.payload.items || []);
      state.columns = normalizeColumns(action.payload.columns || []);
      state.userStory = normalizeStories(action.payload.userStory || []);
      state.loading = false;
    },
    editItemSuccess(state, action) {
      state.items = normalizeItems(action.payload.items || []);
      state.columns = normalizeColumns(action.payload.columns || []);
      state.userStory = normalizeStories(action.payload.userStory || []);
      state.loading = false;
    },
    updateColumnItemOrderSuccess(state, action) {
      state.columns = normalizeColumns(action.payload.columns || []);
      state.loading = false;
    },
    selectItemSuccess(state, action) {
      state.selectedItem =
        action.payload.selectedItem == null ? null : normalizeId(action.payload.selectedItem);
      state.loading = false;
    },
    addItemCommentSuccess(state, action) {
      state.items = normalizeItems(action.payload.items || []);
      state.comments = normalizeComments(action.payload.comments || []);
      state.loading = false;
    },
    deleteItemSuccess(state, action) {
      state.items = normalizeItems(action.payload.items || []);
      state.columns = normalizeColumns(action.payload.columns || []);
      state.userStory = normalizeStories(action.payload.userStory || []);
      state.loading = false;
    },

    // Story mutations
    addStorySuccess(state, action) {
      state.userStory = normalizeStories(action.payload.userStory || []);
      state.userStoryOrder = (action.payload.userStoryOrder || []).map(normalizeId);
      state.loading = false;
    },
    editStorySuccess(state, action) {
      state.userStory = normalizeStories(action.payload.userStory || []);
      state.loading = false;
    },
    updateStoryOrderSuccess(state, action) {
      state.userStoryOrder = (action.payload.userStoryOrder || []).map(normalizeId);
      state.loading = false;
    },
    updateStoryItemOrderSuccess(state, action) {
      state.userStory = normalizeStories(action.payload.userStory || []);
      state.loading = false;
    },
    addStoryCommentSuccess(state, action) {
      state.userStory = normalizeStories(action.payload.userStory || []);
      state.comments = normalizeComments(action.payload.comments || []);
      state.loading = false;
    },
    deleteStorySuccess(state, action) {
      state.userStory = normalizeStories(action.payload.userStory || []);
      state.userStoryOrder = (action.payload.userStoryOrder || []).map(normalizeId);
      state.loading = false;
    }
  }
});

export default slice.reducer;

// ---------- Error helper ----------
const extractError = (e) => e?.response?.data || e?.detail || e?.message || e;

// ---------- GET Thunks ----------
export const getColumns = () => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const r = await axios.get('/kanban/columns');
    dispatch(slice.actions.getColumnsSuccess(r.data.columns));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

export const getColumnsOrder = () => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const r = await axios.get('/kanban/columns-order');
    dispatch(slice.actions.getColumnsOrderSuccess(r.data.columnsOrder));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

export const getComments = () => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const r = await axios.get('/kanban/comments');
    dispatch(slice.actions.getCommentsSuccess(r.data.comments));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

export const getProfiles = () => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const r = await axios.get('/kanban/profiles');
    dispatch(slice.actions.getProfilesSuccess(r.data.profiles));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

export const getItems = () => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const r = await axios.get('/kanban/items');
    dispatch(slice.actions.getItemsSuccess(r.data.items));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

export const getUserStory = () => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const r = await axios.get('/kanban/userstory');
    dispatch(slice.actions.getUserStorySuccess(r.data.userStory));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

export const getUserStoryOrder = () => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const r = await axios.get('/kanban/userstory-order');
    dispatch(slice.actions.getUserStoryOrderSuccess(r.data.userStoryOrder));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

// ---------- Bootstrap (optional global load) ----------
export const bootstrapKanban = () => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const [
      cols,
      colOrder,
      items,
      stories,
      storyOrder,
      comments,
      profiles
    ] = await Promise.all([
      axios.get('/kanban/columns'),
      axios.get('/kanban/columns-order'),
      axios.get('/kanban/items'),
      axios.get('/kanban/userstory'),
      axios.get('/kanban/userstory-order'),
      axios.get('/kanban/comments'),
      axios.get('/kanban/profiles')
    ]);
    dispatch(slice.actions.getColumnsSuccess(cols.data.columns));
    dispatch(slice.actions.getColumnsOrderSuccess(colOrder.data.columnsOrder));
    dispatch(slice.actions.getItemsSuccess(items.data.items));
    dispatch(slice.actions.getUserStorySuccess(stories.data.userStory));
    dispatch(slice.actions.getUserStoryOrderSuccess(storyOrder.data.userStoryOrder));
    dispatch(slice.actions.getCommentsSuccess(comments.data.comments));
    dispatch(slice.actions.getProfilesSuccess(profiles.data.profiles));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

// ---------- Column Mutations ----------
export const addColumn = (title) => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const clean = (title || '').trim();
    if (!clean) throw new Error('Title is required');
    const r = await axios.post('/kanban/add-column', {
      column: { id: 0, title: clean, itemIds: [] },
      columns: [],
      columnsOrder: []
    });
    dispatch(slice.actions.addColumnSuccess(r.data));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

export const editColumn = (column) => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const r = await axios.post('/kanban/edit-column', {
      column: {
        id: normalizeId(column.id),
        title: column.title,
        itemIds: (column.itemIds || []).map(normalizeId)
      },
      columns: []
    });
    dispatch(slice.actions.editColumnSuccess(r.data));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

export const updateColumnOrder = (order) => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const r = await axios.post('/kanban/update-column-order', {
      columnsOrder: (order || []).map(normalizeId)
    });
    dispatch(slice.actions.updateColumnOrderSuccess(r.data));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

export const deleteColumn = (id) => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const r = await axios.post('/kanban/delete-column', {
      columnId: normalizeId(id),
      columnsOrder: [],
      columns: []
    });
    dispatch(slice.actions.deleteColumnSuccess(r.data));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

// ---------- Item Mutations ----------
export const addItem = (columnId, item, storyId = null) => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const r = await axios.post('/kanban/add-item', {
      columnId: normalizeId(columnId),
      columns: [],
      item: {
        id: 0,
        title: item.title,
        dueDate: item.dueDate ?? null,
        image: item.image ?? null,
        assign: item.assign ?? '',
        description: item.description ?? '',
        priority: item.priority ?? 'low',
        attachments: [],
        commentIds: []
      },
      items: [],
      storyId: storyId == null ? null : normalizeId(storyId),
      userStory: []
    });
    dispatch(slice.actions.addItemSuccess(r.data));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

export const editItem = (columnId, item, storyId = null) => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const r = await axios.post('/kanban/edit-item', {
      items: [],
      item: {
        id: normalizeId(item.id),
        title: item.title,
        dueDate: item.dueDate ?? null,
        image: item.image ?? null,
        assign: item.assign ?? '',
        description: item.description ?? '',
        priority: item.priority ?? 'low',
        attachments: item.attachments ?? [],
        commentIds: item.commentIds ?? []
      },
      userStory: [],
      storyId: storyId == null ? null : normalizeId(storyId),
      columns: [],
      columnId: columnId == null ? null : normalizeId(columnId)
    });
    dispatch(slice.actions.editItemSuccess(r.data));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

export const updateColumnItemOrder = (columns) => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const payload = (columns || []).map((c) => ({
      id: normalizeId(c.id),
      title: c.title,
      itemIds: (c.itemIds || []).map(normalizeId)
    }));
    const r = await axios.post('/kanban/update-item-order', { columns: payload });
    dispatch(slice.actions.updateColumnItemOrderSuccess(r.data));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

export const selectItem = (selectedItem) => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const r = await axios.post('/kanban/select-item', {
      selectedItem: selectedItem == null ? null : selectedItem
    });
    dispatch(slice.actions.selectItemSuccess(r.data));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

export const addItemComment = (itemId, comment) => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const r = await axios.post('/kanban/add-item-comment', {
      items: [],
      itemId: normalizeId(itemId),
      comment,
      comments: []
    });
    dispatch(slice.actions.addItemCommentSuccess(r.data));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

export const deleteItem = (itemId) => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const r = await axios.post('/kanban/delete-item', {
      columns: [],
      itemId: normalizeId(itemId),
      userStory: [],
      items: []
    });
    dispatch(slice.actions.deleteItemSuccess(r.data));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

// ---------- Story Mutations ----------
export const addStory = (title) => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const r = await axios.post('/kanban/add-story', {
      userStory: [],
      story: { id: 0, title, itemIds: [], commentIds: [] },
      userStoryOrder: []
    });
    dispatch(slice.actions.addStorySuccess(r.data));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

export const editStory = (story) => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const r = await axios.post('/kanban/edit-story', {
      userStory: [],
      story: {
        id: normalizeId(story.id),
        title: story.title,
        itemIds: story.itemIds || [],
        commentIds: story.commentIds || []
      }
    });
    dispatch(slice.actions.editStorySuccess(r.data));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

export const updateStoryOrder = (order) => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const r = await axios.post('/kanban/update-story-order', {
      userStoryOrder: (order || []).map(normalizeId)
    });
    dispatch(slice.actions.updateStoryOrderSuccess(r.data));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

export const updateStoryItemOrder = (stories) => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const payload = (stories || []).map((s) => ({
      id: normalizeId(s.id),
      title: s.title,
      itemIds: (s.itemIds || []).map(normalizeId),
      commentIds: (s.commentIds || []).map(normalizeId)
    }));
    const r = await axios.post('/kanban/update-storyitem-order', { userStory: payload });
    dispatch(slice.actions.updateStoryItemOrderSuccess(r.data));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

export const addStoryComment = (storyId, comment) => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const r = await axios.post('/kanban/add-story-comment', {
      userStory: [],
      storyId: normalizeId(storyId),
      comment,
      comments: []
    });
    dispatch(slice.actions.addStoryCommentSuccess(r.data));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

export const deleteStory = (storyId) => async (dispatch) => {
  dispatch(slice.actions.startLoading());
  try {
    const r = await axios.post('/kanban/delete-story', {
      userStory: [],
      storyId: normalizeId(storyId),
      userStoryOrder: []
    });
    dispatch(slice.actions.deleteStorySuccess(r.data));
  } catch (e) {
    dispatch(slice.actions.hasError(extractError(e)));
  }
};

// ---------- Selectors ----------
export const selectKanban         = (state) => state.kanban || kanbanInitialState;
export const selectColumns        = (state) => state.kanban?.columns ?? kanbanInitialState.columns;
export const selectColumnsOrder   = (state) => state.kanban?.columnsOrder ?? kanbanInitialState.columnsOrder;
export const selectItems          = (state) => state.kanban?.items ?? kanbanInitialState.items;
export const selectComments       = (state) => state.kanban?.comments ?? kanbanInitialState.comments;
export const selectProfiles       = (state) => state.kanban?.profiles ?? kanbanInitialState.profiles;
export const selectUserStory      = (state) => state.kanban?.userStory ?? kanbanInitialState.userStory;
export const selectUserStoryOrder = (state) => state.kanban?.userStoryOrder ?? kanbanInitialState.userStoryOrder;
export const selectSelectedItem   = (state) => state.kanban?.selectedItem ?? kanbanInitialState.selectedItem;
export const selectLoading        = (state) => state.kanban?.loading ?? kanbanInitialState.loading;
export const selectError          = (state) => state.kanban?.error ?? kanbanInitialState.error;